import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';

import {BehaviorSubject, EMPTY, map, merge, Observable, of, Subject, switchMap, tap} from 'rxjs';
import {AntragTicketDTO} from "../../../models/antrag-ticket/AntragTicketDTO";
import {AntragTicketUpdateDTO} from "../../../models/antrag-ticket/AntragTicketUpdateDTO";
import {HttpHeaderService} from "../../utils/http/http-header.service";
import {AntragTicketMinimalDTO} from "../../../models/antrag-ticket/AntragTicketMinimalDTO";
import {catchError, takeUntil} from "rxjs/operators";
import {CustomToastService} from "../../utils/custom-toast.service";
import {AntragAttachmentAdjustDto} from "../../../models/antrag-ticket/AntragAttachmentAdjustDto";
import {AntragAttachmentCreateDto} from "../../../models/antrag-ticket/AntragAttachmentCreateDto";
import {TranslateService} from "@ngx-translate/core";
import {extractFilenameFromContentDisposition} from "../../utils/content-disposition.util";
import {downloadBlob} from "../../utils/blob-download.util";

@Injectable({
  providedIn: 'root'
})
export class AntragTicketService {

  configUrl = 'api/antrag-ticket';

  private antragObs$$ = new BehaviorSubject<AntragTicketDTO>(null);
  public antragObs$: Observable<AntragTicketDTO> = this.antragObs$$.asObservable();

  public setAntragID$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private destroy$ = new Subject<boolean>();

  constructor(
    private readonly http: HttpClient,
    private readonly headerService: HttpHeaderService,
    private readonly customToastService: CustomToastService,
    private readonly translateService: TranslateService
  ) {
    this.setAntragID$.pipe(
      takeUntil(this.destroy$),
      switchMap((val) =>
            merge(
              this.loadAntrag(val).pipe(
                catchError(() => {
                  this.customToastService.showError('Der Antrag konnte nicht geladen werden.');
                  return EMPTY;
                }),
                tap((antrag) => this.antragObs$$.next(antrag))
              )
            )
      )
    ).subscribe();
  }

  getAllAntraegeMinimal(): Observable<AntragTicketMinimalDTO[]> {
    return this.http.get<AntragTicketMinimalDTO[]>(
      this.configUrl+'/bulk-minimal',
      { headers: this.headerService.getAuthHeaderApplicationJson()}
    );
  }

  private loadAntrag(id: string): Observable<AntragTicketDTO> {
    if(!id){
      return of(null);
    }
    return this.http.get<AntragTicketDTO>(
      this.configUrl+'/'+id,
      { headers: this.headerService.getAuthHeaderApplicationJson() }
    );
  }

  updateAntrag(id: number, dto: AntragTicketUpdateDTO): Observable<any> {
    const formData = new FormData();
    formData.append('dto', new Blob([JSON.stringify(dto)], { type: 'application/json'}));
    return this.http.patch<AntragTicketDTO>(
      this.configUrl+'/'+id,
      formData,
      { headers: this.headerService.getAuthHeader() }
    )
      .pipe( map(antrag => {
        this.setAntragID$.next(antrag.id.toString());
        return antrag;
      }));
  }

  createNewEntwurf(): Observable<number>{
    return this.http.post<number>(
      this.configUrl+ '/create',
      null,
      { headers: this.headerService.getAuthHeader() }
    );
  }


  deleteAntrag(id: number): Observable<any> {
    return this.http.delete(
      this.configUrl+'/'+id,
      { headers: this.headerService.getAuthHeader(), responseType: 'text' }
    );
  }

  getVorgaenger(schuelerId: number): Observable<any> {
    if (schuelerId !== null) {
      return this.http.get<AntragTicketDTO>(
        this.configUrl+'/vorgaenger?schueler-id='+schuelerId,
        { headers: this.headerService.getAuthHeaderApplicationJson() }
      );
    } else {
      return this.http.get<AntragTicketDTO>(
        this.configUrl+'/vorgaenger',
        { headers: this.headerService.getAuthHeaderApplicationJson() }
      );
    }
  }

  downloadAttachment(antragUuid: string, attachmentUuid: string) {
    let downloadFileName = "";
    this.http.get<Blob>(this.configUrl+'/'+antragUuid+'/download-attachment/'+attachmentUuid, {headers: this.headerService.getAuthHeader(), observe: 'response', responseType: 'blob' as 'json'})
      .pipe(
        catchError(()  => {
          this.customToastService.showError("Der Anhang konnte nicht heruntergeladen werden.");
          return EMPTY;
        })
      )
      .subscribe(response => {
        const contentDisposition = response.headers.get("Content-Disposition")
        downloadFileName = extractFilenameFromContentDisposition(contentDisposition, downloadFileName);
        const blob = (response as HttpResponse<Blob>).body as Blob;
        downloadBlob(blob, downloadFileName);
      });
  }

  removeAttachment(antragUuid: string, attachmentUuid: string): void {
    this.http.post<AntragTicketDTO>(this.configUrl+'/'+antragUuid+'/removeAttachment/'+attachmentUuid, null, {headers: this.headerService.getAuthHeader()})
      .subscribe(antrag => {
        this.setAntragID$.next(antrag.id.toString());
      })
  }

  addAttachment(antragUuid: string,  file: File, dto: AntragAttachmentCreateDto): void{
    const formData = new FormData();
    formData.append('file',file);
    formData.append('dto', new Blob([JSON.stringify(dto)], {type: "application/json"}));
    this.http.post<AntragTicketDTO>(this.configUrl+'/'+antragUuid+'/addAttachment', formData, {headers: this.headerService.getAuthHeader() })
      .pipe(
        catchError(err => {
          if (err.status === 400) {
            this.customToastService.showError(this.translateService.instant('ATTACHMENT.ERRORS.'+ err.error?.message));
          }
          if (err.status === 422) {
            for (const error of err.error) {
              console.log('err', error);
              let validationParams = null;
              if (error.error === 'FILEVALIDATION_FILE_BAD_CONTENTTYPE') {
                validationParams = {'validContentTypes': error.details?.validContentTypes};
              } else if (error.error === 'FILEVALIDATION_FILENAME_BAD_EXTENSION') {
                validationParams = {'validExtensions': error.details?.validExtensions};
              } else if (error.error === 'FILEVALIDATION_FILENAME_TOO_LONG'
                || error.error === 'FILEVALIDATION_FILENAME_BAD_CHARS'
                || error.error === 'FILEVALIDATION_ALT_FILENAME_TOO_LONG'
                || error.error === 'FILEVALIDATION_ALT_FILENAME_BAD_CHARS'
              ) {
                validationParams = {'need': error.details?.need};
              }
              this.customToastService.showError(this.translateService.instant('ATTACHMENT.ERRORS.'+ error.error, validationParams));
            }
          }
          return EMPTY;
        })
      )
      .subscribe(antrag => {
        this.setAntragID$.next(antrag.id.toString());
      })
  }

  adjustAttachmentType(antragUuid: string, dto: AntragAttachmentAdjustDto): void{
    this.http.post<AntragTicketDTO>(this.configUrl+'/'+antragUuid+'/adjustAttachment', dto, {headers: this.headerService.getAuthHeader()})
      .subscribe(antrag => {
        this.setAntragID$.next(antrag.id.toString());
      })
  }

}
